import { apiClient } from './http/client'

export type ConfigurationFile = { path: string; content: string; sha256: string }
export type EditorMetadata = {
  categories: { key: string; title: string; directory?: string | null; icon_key?: string | null }[]
  entries: { path: string; label: string; category_key: string; icon_key?: string | null }[]
  workflow_positions: Record<string, Record<string, { x: number; y: number }>>
}
export type ConfigurationSnapshot = {
  tenant_id: string; instance_id: string; status: 'active' | 'initial'; active_revision: number
  content_digest: string; editor_digest: string; files: ConfigurationFile[]
  editor_metadata?: EditorMetadata | null; available_capability_ids?: string[]
}
export type FileReplacement = { path: string; expected_sha256: string; content: string }
export type ConfigurationApplyCommand = {
  expected_active_revision: number; expected_editor_digest: string; request_id: string; changes: FileReplacement[]
  creations?: { path: string; content: string }[]; deletions?: { path: string; expected_sha256: string }[]
  editor_metadata?: EditorMetadata
}
export type ConfigurationApplyResult = {
  request_id: string; status: 'active' | 'superseded'; admission_reference: string
  deployment_reference: string; configuration: ConfigurationSnapshot
}

const record = (value: unknown): value is Record<string, unknown> => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
)
const text = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0
const invalid = (): never => { throw new TypeError('配置响应不可用') }
const optionalIcon = (value: unknown) => value == null || (typeof value === 'string' && /^\S{1,128}$/.test(value))
const validMetadata = (value: unknown): boolean => record(value) && Array.isArray(value.categories)
  && value.categories.every((item) => record(item) && text(item.key) && text(item.title)
    && (item.directory == null || text(item.directory)) && optionalIcon(item.icon_key))
  && Array.isArray(value.entries) && value.entries.every((item) => record(item) && text(item.path) && text(item.label) && text(item.category_key) && optionalIcon(item.icon_key))
  && record(value.workflow_positions) && Object.values(value.workflow_positions).every((positions) => record(positions)
    && Object.values(positions).every((position) => record(position) && typeof position.x === 'number'
      && typeof position.y === 'number' && Number.isFinite(position.x) && Number.isFinite(position.y)))
const configuration = (value: unknown, instanceId: string): ConfigurationSnapshot => {
  if (!record(value) || value.instance_id !== instanceId || !text(value.tenant_id)
    || typeof value.status !== 'string' || !['active', 'initial'].includes(value.status) || !Number.isSafeInteger(value.active_revision)
    || Number(value.active_revision) < 0 || !text(value.content_digest) || !text(value.editor_digest)
    || !Array.isArray(value.files) || !value.files.every((file) => record(file)
      && text(file.path) && typeof file.content === 'string' && text(file.sha256))
    || (value.editor_metadata != null && !validMetadata(value.editor_metadata))
    || (value.available_capability_ids !== undefined && (!Array.isArray(value.available_capability_ids)
      || !value.available_capability_ids.every(text)))) return invalid()
  const paths = value.files.map((file) => file.path)
  if (new Set(paths).size !== paths.length) return invalid()
  return value as ConfigurationSnapshot
}
const endpoint = (instanceId: string) => `/instance-admin/instances/${encodeURIComponent(instanceId)}/configuration`

export const configurationEditingApi = {
  read: async (instanceId: string, signal?: AbortSignal): Promise<ConfigurationSnapshot> => {
    const { data } = await apiClient.get<unknown>(endpoint(instanceId), { signal })
    return configuration(data, instanceId)
  },
  apply: async (instanceId: string, command: ConfigurationApplyCommand, signal?: AbortSignal): Promise<ConfigurationApplyResult> => {
    const { data } = await apiClient.put<unknown>(endpoint(instanceId), command, { signal })
    if (!record(data) || data.request_id !== command.request_id || typeof data.status !== 'string' || !['active', 'superseded'].includes(data.status)
      || !text(data.admission_reference) || !text(data.deployment_reference)) return invalid()
    return { request_id: command.request_id, status: data.status as ConfigurationApplyResult['status'],
      admission_reference: data.admission_reference, deployment_reference: data.deployment_reference,
      configuration: configuration(data.configuration, instanceId) }
  },
}
